import { IMapLayerProvider } from 'app/config/IMapLayerProvider';
import { IMapLayerProviderPersister } from 'app/overview/config/configuration_tab/general/frontend_config/common/IMapLayerProviderPersister';
import { EventRegistry } from 'app/shared/events/event_registry';
import {
    FrontEndConfigFormField,
    FrontEndConfigFormModel,
} from 'app/overview/config/configuration_tab/general/frontend_config/FrontEndConfigFormModel';
import { IFormFieldModel } from 'app/overview/config/configuration_tab/general/frontend_config/IFormFieldModel';
import { AbstractMapLayerProviderList } from 'app/config/AbstractMapLayerProviderList';
import { IMapLayerProviderFormModel } from 'app/overview/config/configuration_tab/general/frontend_config/common/IMapLayerProviderFormModel';

interface MapLayerProviderListFormViewState<
    T extends IMapLayerProvider,
    M extends IMapLayerProviderFormModel<T>,
> {
    providerForms: M[];
    // is the form valid
    isValid: boolean;
    // is the form dirty
    isDirty: boolean;
    // is the form busy (i.e. async operation in progress)
    isBusy: boolean;
    deletedLayerNames: string[];
}

// Just for testability
type MapLayerProviderFormModelFactoryFn<
    T extends IMapLayerProvider,
    M extends IMapLayerProviderFormModel<T>,
> = (changeHandler: IMapLayerProviderPersister, origValue: T, valueUnderEdit: T) => M;

export interface MapLayerProviderListFormModelOptions<
    T extends IMapLayerProvider,
    M extends IMapLayerProviderFormModel<T>,
> {
    // Just for testability
    mapLayerProviderFormModelFactoryFn?: MapLayerProviderFormModelFactoryFn<T, M>;
}

export abstract class AbstractMapLayerProviderListFormModel<
        T extends IMapLayerProvider,
        L extends AbstractMapLayerProviderList<T>,
        M extends IMapLayerProviderFormModel<T>,
    >
    implements IMapLayerProviderPersister, IFormFieldModel<MapLayerProviderListFormViewState<T, M>>
{
    protected _origValue: L;
    protected _valueUnderEdit: L;
    protected _providerFormModels: M[] = [];

    private _formField: FrontEndConfigFormField;
    private _frontEndConfigFormFieldModel;
    private _onChange: EventRegistry;
    private _hasBeenInitialised: boolean = false;
    private _destroyListener: () => void;

    // Use this over making an actual map layer provider model - used for unit testing
    private _mapLayerProviderModelFactoryFn: MapLayerProviderFormModelFactoryFn<T, M>;

    private _isBusy: boolean = false;

    constructor(
        frontEndConfigFormModel: FrontEndConfigFormModel,
        formField: FrontEndConfigFormField,
        options: MapLayerProviderListFormModelOptions<T, M> = {},
    ) {
        if (options.mapLayerProviderFormModelFactoryFn) {
            this._mapLayerProviderModelFactoryFn = options.mapLayerProviderFormModelFactoryFn;
        }

        this._frontEndConfigFormFieldModel = frontEndConfigFormModel;
        this._formField = formField;
        this._onChange = new EventRegistry();
        this._destroyListener = frontEndConfigFormModel.onFieldChange(formField, (newValue) => {
            this._origValue = newValue.data;
            this._origValue.mapLayerProviders.forEach((origProvider, index) => {
                // 1. Match each orig provider up with a current provider form model
                const form = this._providerFormModels.find(
                    (model) => model.uuid === origProvider.uuid,
                );
                if (form) {
                    // The form exists, update its original value...
                    form.updateOrigValue(origProvider);
                } else {
                    // 2. Create the new sub forms for newly added items from the API
                    const newModel = this._createProviderFormModel(
                        origProvider,
                        origProvider.clone(),
                    );
                    // Make sure the index is reasonable
                    index = Math.min(Math.max(0, this._providerFormModels.length), index);
                    this._providerFormModels.splice(index, 0, newModel);
                }
            });

            // 3. Delete existing sub forms for deleted items from the API
            // Only apply to clean forms
            const cleanForms = this._providerFormModels.filter((formModel) => !formModel.isDirty());
            // If these clean forms dont exist in the new orig value, dump them.
            cleanForms.forEach((formModel) => {
                if (!this._origValue.findByUuid(formModel.uuid)) {
                    // splice out the sub form
                    this.deleteProviderForm(formModel.uuid);
                }
            });

            if (!this._hasBeenInitialised) {
                this._hasBeenInitialised = true;
                this.revert();
            } else {
                this._notifyListeners();
            }
        });
        // self-add, will self-remove when destroy() is called
        this._frontEndConfigFormFieldModel.addFormFieldModel(this);
    }

    /**
     * Add a new provider. Used when the 'add' button is clicked
     */
    public abstract addNewProvider(): void;

    /**
     * The subclass needs to specify how to make the form models that will populate this list
     * @param {T} origValue
     * @param {T} valueUnderEdit
     * @returns {M}
     */
    protected abstract createProviderFormModel(origValue: T, valueUnderEdit: T): M;

    /**
     * This weird construct is for the sake of testability.
     * Call this function internally. It will divert the actual call to either the
     * implemented abstract function (the prod behaviour) or to the factoryFn if it exists
     * (the test behaviour)
     *
     * @param {T} origValue
     * @param {T} valueUnderEdit
     * @returns {M}
     * @private
     */
    protected _createProviderFormModel(origValue: T, valueUnderEdit: T) {
        if (this._mapLayerProviderModelFactoryFn) {
            return this._mapLayerProviderModelFactoryFn(this, origValue, valueUnderEdit);
        }
        return this.createProviderFormModel(origValue, valueUnderEdit);
    }

    /**
     * Turn the list form model object back into a list object, containing all of the
     * edited values. We use the result of this function to save the contents of the list to the API
     * @returns {L}
     */
    protected abstract createMapLayerListProviderFromValueUnderEdit(): L;

    getId(): string {
        return this._formField;
    }

    /**
     *
     * @param callback callback func
     * @returns {() => void} destroy listener anon func
     */
    onChange(callback): () => void {
        const result = this._onChange.addEventListener(callback);
        this._notifyListeners();
        return result;
    }

    providerFormUpdated(): void {
        this._notifyListeners();
    }

    deleteProviderForm(uuid: string): void {
        this._providerFormModels = this._providerFormModels.filter((m) => m.uuid !== uuid);
        this._notifyListeners();
    }

    getViewState(): MapLayerProviderListFormViewState<T, M> {
        // Check if any items have been removed...
        const currentUuids = this._providerFormModels.map((formModel) => formModel.uuid);
        const missingNames = this._origValue.mapLayerProviders.reduce((accum, mapLayerProvider) => {
            if (!currentUuids.includes(mapLayerProvider.uuid)) {
                accum.push(mapLayerProvider.name);
            }
            return accum;
        }, []);

        return {
            providerForms: this._providerFormModels,
            isValid: this.isValid(),
            isDirty: this.isDirty(),
            isBusy: this._isBusy,
            deletedLayerNames: missingNames,
        };
    }

    isDirty(): boolean {
        const childrenDirty =
            this._providerFormModels.length > 0
                ? this._providerFormModels.some((m) => m.isDirty())
                : false;
        const changeInLength =
            this._providerFormModels.length !== this._origValue.mapLayerProviders.length;
        let changeInOrder = false;
        if (!changeInLength) {
            // detect change in order, i.e. are the uuids in the same
            // order as when we started.
            // Also detects whether the overall uuids have changed. i.e.
            // a delete followed by an add
            for (let i = 0; i < this._origValue.mapLayerProviders.length; ++i) {
                const oldUuidAtIndex = this._origValue.mapLayerProviders[i].uuid;
                const newUuidAtIndex = this._providerFormModels[i].uuid;
                if (oldUuidAtIndex !== newUuidAtIndex) {
                    changeInOrder = true;
                    // dont need to continue searching
                    break;
                }
            }
        }
        return childrenDirty || changeInLength || changeInOrder;
    }

    async save(): Promise<any> {
        // Collect all of the providers under edit
        try {
            this._isBusy = true;
            this._notifyListeners();
            return await this._frontEndConfigFormFieldModel.saveFormField(
                this._formField,
                this.createMapLayerListProviderFromValueUnderEdit(),
            );
        } finally {
            this._isBusy = false;
            this._notifyListeners();
        }
    }

    destroy(): void {
        this._destroyListener();
        this._frontEndConfigFormFieldModel.removeFormFieldModel(this as any);
    }

    isValid(): boolean {
        return this._providerFormModels.length > 0
            ? this._providerFormModels.every((m) => m.isValid())
            : true;
    }

    revert(): void {
        this._valueUnderEdit = this._origValue.clone();
        // Reset all of the form models by creating new ones
        this._providerFormModels = this._valueUnderEdit.mapLayerProviders.map((p) =>
            this._createProviderFormModel(this._origValue.findByUuid(p.uuid), p),
        );
        this._notifyListeners();
    }

    protected _notifyListeners() {
        this._onChange.notifyListeners(this.getViewState());
    }
}
